import { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, MessagesSquare } from "lucide-react";
import { Brand } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { AcornJar } from "@/components/AcornJar";
import { PracticeChat } from "@/components/PracticeChat";
import { MilestoneCelebration } from "@/components/MilestoneCelebration";
import { useFamilyProfile } from "@/hooks/useFamilyProfile";
import { useAcornJar } from "@/hooks/useAcornJar";
import { findScenario } from "@/lib/manners";
import { PrintableCards } from "@/components/PrintableCards";
import { CARD_SETS } from "@/lib/cards";
import { MusicHandoff } from "@/components/MusicHandoff";
import { CapReferral } from "@/components/CapReferral";

export default function MannersScenario() {
  const { id } = useParams();
  const scenario = findScenario(id);
  const age = "kid";
  const { profile } = useFamilyProfile();
  const { jar, refresh, milestone, dismissMilestone } = useAcornJar(profile);
  const [variantId, setVariantId] = useState(scenario?.variants?.[0].id);
  const variant = scenario?.variants?.find((v) => v.id === variantId);
  if (!scenario) return <Navigate to="/manners" replace />;
  const Icon = scenario.icon;

  return (
    <div className={`pp-app mode-${age}`} data-testid={`scenario-page-${id}`}>
      <header className="pp-header">
        <Brand />
        <div className="flex items-center gap-2">
          <AcornJar jar={jar} />
          <Link to="/manners" className="pp-ghost" data-testid="scenario-back-link"><ArrowLeft className="h-4 w-4" /> All manners</Link>
        </div>
      </header>
      <main className="pp-main pp-wide">
        <p className="pp-label">Everyday manners{profile ? ` · ${profile.name}` : ""}</p>
        <div className="flex items-center gap-4">
          <span className={`pp-scenario-icon s-${id}`}><Icon className="h-7 w-7" /></span>
          <h1 className="pp-h1">{scenario.title}</h1>
        </div>
        <div className="pp-scenario-layout">
          <section className="pp-card" data-testid="scenario-tips">
            <h2 className="pp-h2">Key manners</h2>
            <ol className="pp-steps">
              {scenario.tips.map(([t, d], i) => <li key={t}><span>{i + 1}</span><p><strong className="pp-step-title">{/[.?!”]$/.test(t) ? t : `${t}.`}</strong> {d}</p></li>)}
            </ol>
          </section>
          <div className="grid gap-4">
            {scenario.variants && (
              <div className="pp-presets" role="tablist" data-testid="practice-variant-picker">
                <span className="pp-label w-full">Who is your note for?</span>
                {scenario.variants.map((v) => (
                  <button key={v.id} className={`pp-pill ${variantId === v.id ? "on" : ""}`} onClick={() => setVariantId(v.id)}
                    data-testid={`practice-variant-${v.id}`}>{v.label}</button>
                ))}
              </div>
            )}
            <PracticeChat key={`${id}-${variantId || ""}`} scenario={scenario} variant={variant} age={age} profile={profile}
              onEarned={() => setTimeout(refresh, 3400)} />
          </div>
        </div>
        {id === "table" && (
          <Link to="/dinner" className="pp-card pp-dinner-promo mt-8" data-testid="table-dinner-game-link">
            <span className="pp-scenario-icon s-dinner"><MessagesSquare className="h-7 w-7" /></span>
            <div>
              <h2 className="pp-h2">Try tonight: Family Dinner Game</h2>
              <p className="opacity-75">A question game that engages every family member.</p>
            </div>
          </Link>
        )}
        {id === "music" && <MusicHandoff />}
        {(scenario.referral || id === "friends") && <CapReferral />}
        {CARD_SETS[id] && <PrintableCards names={CARD_SETS[id]} title={`${scenario.title}: post, print, share`} />}
      </main>
      <Footer />
      <MilestoneCelebration milestone={milestone} name={profile?.name} onClose={dismissMilestone} />
    </div>
  );
}
